import { useState, useEffect } from "react";
import { apiClient } from "../../lib/api";
import { LayoutSection, Illustration } from "../../lib/types";
import { ImagePlacementList } from "./ImagePlacementList";

interface AdditionalConfig {
  link?: string;
  isFeatureImage: boolean;
}

export interface ImagePlacement {
  id: number;
  image_id: string;
  section_id: number;
  sort_order: number;
  additional_config: AdditionalConfig;
  created_at: string;
  illustration: {
    id: string;
    title: string;
    storage_path: string;
  };
  layout_section: {
    id: number;
    section_name: string;
  };
}

export function ImagePlacementManager() {
  const [placements, setPlacements] = useState<ImagePlacement[]>([]);
  const [sections, setSections] = useState<LayoutSection[]>([]);
  const [illustrations, setIllustrations] = useState<Illustration[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newPlacement, setNewPlacement] = useState({
    image_id: "",
    section_id: 0,
    sort_order: 0,
    additional_config: {
      link: "",
      isFeatureImage: false,
    },
  });

  // データの取得
  const fetchData = async () => {
    try {
      // レイアウトセクションの取得
      const sectionsData = await apiClient.get("/layout_sections") as LayoutSection[];
      setSections(sectionsData);

      // イラストの取得
      const illustrationsData = await apiClient.get("/illustrations") as Illustration[];
      setIllustrations(illustrationsData);

      // 配置情報の取得
      const placementsData = await apiClient.get("/image_placements") as any[];
      setPlacements(placementsData);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // セクション変更時のハンドラ
  const handleSectionChange = (sectionId: number) => {
    if (!sectionId) {
      setNewPlacement({
        ...newPlacement,
        section_id: 0,
        sort_order: 0,
        additional_config: {
          link: "",
          isFeatureImage: false,
        },
      });
      return;
    }

    const targetSection = sections.find((s) => s.id === sectionId);
    if (!targetSection) return;

    const sectionPlacements = placements.filter(
      (p) => p.section_id === sectionId
    );
    if (sectionPlacements.length >= targetSection.max_images) {
      setError(
        `このセクションには既に${targetSection.max_images}枚の画像が配置されています。新しく配置するには、既存の画像を削除してください。`
      );
      setTimeout(() => setError(null), 2000);

      // セクション選択をリセット
      setNewPlacement({
        ...newPlacement,
        section_id: 0,
        sort_order: 0,
        additional_config: {
          link: "",
          isFeatureImage: false,
        },
      });
      return;
    }

    // 利用可能な最小の順序を取得
    const availableOrders = getAvailableOrders(sectionId);
    setNewPlacement({
      ...newPlacement,
      section_id: sectionId,
      sort_order: availableOrders[0] || 0,
    });
  };

  // 選択可能な順序を計算
  const getAvailableOrders = (sectionId: number): number[] => {
    if (!sectionId) return [];

    const targetSection = sections.find((s) => s.id === sectionId);
    if (!targetSection) return [];

    const sectionPlacements = placements.filter(
      (p) => p.section_id === sectionId
    );
    const usedOrders = new Set(sectionPlacements.map((p) => p.sort_order));

    // 0からmax_images-1までの範囲で、未使用の順序を返す
    return Array.from({ length: targetSection.max_images }, (_, i) => i).filter(
      (order) => !usedOrders.has(order)
    );
  };

  // 新規配置の追加
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      // セクションの最大画像数チェック
      const targetSection = sections.find(
        (s) => s.id === newPlacement.section_id
      );
      if (!targetSection) {
        throw new Error("セクションが見つかりません");
      }

      const currentCount = placements.filter(
        (p) => p.section_id === newPlacement.section_id
      ).length;
      if (currentCount >= targetSection.max_images) {
        throw new Error(
          `このセクションには最大${targetSection.max_images}枚までしか配置できません`
        );
      }

      await apiClient.post("/image_placements", newPlacement);

      setSuccess("画像を配置しました");
      setTimeout(() => setSuccess(null), 3000);

      // フォームをリセット
      setNewPlacement({
        image_id: "",
        section_id: 0,
        sort_order: 0,
        additional_config: {
          link: "",
          isFeatureImage: false,
        },
      });

      // 一覧を更新
      fetchData();
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // 配置の削除
  const handleDelete = async (id: number) => {
    if (!window.confirm("この配置を削除してもよろしいですか？")) {
      return;
    }

    try {
      await apiClient.delete(`/image_placements/${id}`);
      setSuccess("配置を削除しました");
      setTimeout(() => setSuccess(null), 3000);
      fetchData();
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="p-4">
      <div className="space-y-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            {success}
          </div>
        )}

        {/* 新規配置フォーム */}
        <form
          onSubmit={handleSubmit}
          className="border rounded-md p-4 space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                セクション <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlacement.section_id || ""}
                onChange={(e) => handleSectionChange(parseInt(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">選択してください</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.section_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                イラスト <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlacement.image_id}
                onChange={(e) =>
                  setNewPlacement({
                    ...newPlacement,
                    image_id: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">選択してください</option>
                {illustrations.map((illustration) => (
                  <option key={illustration.id} value={illustration.id}>
                    {illustration.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                表示順序 <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlacement.sort_order}
                onChange={(e) =>
                  setNewPlacement({
                    ...newPlacement,
                    sort_order: parseInt(e.target.value),
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
                disabled={!newPlacement.section_id}
              >
                {getAvailableOrders(newPlacement.section_id).map((order) => (
                  <option key={order} value={order}>
                    {order}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                リンク
              </label>
              <input
                type="text"
                value={newPlacement.additional_config.link}
                onChange={(e) =>
                  setNewPlacement({
                    ...newPlacement,
                    additional_config: {
                      ...newPlacement.additional_config,
                      link: e.target.value,
                    },
                  })
                }
                placeholder="https://example.com"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div className="space-y-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="isFeatureImage"
                checked={newPlacement.additional_config.isFeatureImage}
                onChange={(e) =>
                  setNewPlacement({
                    ...newPlacement,
                    additional_config: {
                      ...newPlacement.additional_config,
                      isFeatureImage: e.target.checked,
                    },
                  })
                }
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="isFeatureImage" className="text-sm font-medium text-gray-700">
                サイズ大
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            画像・アイコンを追加
          </button>
        </form>

        {/* 配置一覧 */}
        <ImagePlacementList
          sections={sections}
          placements={placements}
          onDelete={handleDelete}
          onUpdate={fetchData}
        />
      </div>
    </div>
  );
}
